import { ref } from 'vue'
import {
  doc,
  getDoc,
  collection,
  addDoc,
  query,
  where,
  orderBy,
  getDocs,
  updateDoc,
  deleteDoc
} from 'firebase/firestore'
import { db } from '@/firebase/firebase'
import { useAuth } from '@/composables/common/useAuth'

// 한국 시간(KST) 가져오기
const getKoreanTime = () => {
  const now = new Date()
  const koreaTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Seoul' }))
  const year = koreaTime.getFullYear()
  const month = String(koreaTime.getMonth() + 1).padStart(2, '0')
  const day = String(koreaTime.getDate()).padStart(2, '0')
  const hours = String(koreaTime.getHours()).padStart(2, '0')
  const minutes = String(koreaTime.getMinutes()).padStart(2, '0')

  return {
    date: `${year}-${month}-${day}`,
    time: `${hours}:${minutes}`,
    dateTime: koreaTime
  }
}

// 1주일 후 날짜 계산
const getOneWeekLater = (startDate) => {
  const start = new Date(startDate)
  const end = new Date(start)
  end.setDate(end.getDate() + 6) // 시작일 포함 7일
  const year = end.getFullYear()
  const month = String(end.getMonth() + 1).padStart(2, '0')
  const day = String(end.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function useJump() {
  const { user } = useAuth()

  const myJumps = ref([])
  const activeJump = ref(null) // 현재 활성 점프
  const loading = ref(false)
  const error = ref(null)

  // 시간대 옵션 (새벽은 1시간, 나머지는 30분)
  const timeSlotOptions = [
    { id: 'dawn_1', start: '00:00', end: '01:00', label: '새벽 00:00~01:00', duration: 60 },
    { id: 'dawn_2', start: '01:00', end: '02:00', label: '새벽 01:00~02:00', duration: 60 },
    { id: 'dawn_3', start: '02:00', end: '03:00', label: '새벽 02:00~03:00', duration: 60 },
    { id: 'dawn_4', start: '03:00', end: '04:00', label: '새벽 03:00~04:00', duration: 60 },
    { id: 'dawn_5', start: '04:00', end: '05:00', label: '새벽 04:00~05:00', duration: 60 },
    { id: 'dawn_6', start: '05:00', end: '06:00', label: '새벽 05:00~06:00', duration: 60 },
    { id: 'morning_1', start: '06:00', end: '06:30', label: '아침 06:00~06:30', duration: 30 },
    { id: 'morning_2', start: '06:30', end: '07:00', label: '아침 06:30~07:00', duration: 30 },
    { id: 'morning_3', start: '07:00', end: '07:30', label: '아침 07:00~07:30', duration: 30 },
    { id: 'morning_4', start: '07:30', end: '08:00', label: '아침 07:30~08:00', duration: 30 },
    { id: 'morning_5', start: '08:00', end: '08:30', label: '아침 08:00~08:30', duration: 30 },
    { id: 'morning_6', start: '08:30', end: '09:00', label: '아침 08:30~09:00', duration: 30 },
    { id: 'am_1', start: '09:00', end: '09:30', label: '오전 09:00~09:30', duration: 30 },
    { id: 'am_2', start: '09:30', end: '10:00', label: '오전 09:30~10:00', duration: 30 },
    { id: 'am_3', start: '10:00', end: '10:30', label: '오전 10:00~10:30', duration: 30 },
    { id: 'am_4', start: '10:30', end: '11:00', label: '오전 10:30~11:00', duration: 30 },
    { id: 'am_5', start: '11:00', end: '11:30', label: '오전 11:00~11:30', duration: 30 },
    { id: 'am_6', start: '11:30', end: '12:00', label: '오전 11:30~12:00', duration: 30 },
    { id: 'lunch_1', start: '12:00', end: '12:30', label: '점심 12:00~12:30', duration: 30 },
    { id: 'lunch_2', start: '12:30', end: '13:00', label: '점심 12:30~13:00', duration: 30 },
    { id: 'lunch_3', start: '13:00', end: '13:30', label: '점심 13:00~13:30', duration: 30 },
    { id: 'lunch_4', start: '13:30', end: '14:00', label: '점심 13:30~14:00', duration: 30 },
    { id: 'pm_1', start: '14:00', end: '14:30', label: '오후 14:00~14:30', duration: 30 },
    { id: 'pm_2', start: '14:30', end: '15:00', label: '오후 14:30~15:00', duration: 30 },
    { id: 'pm_3', start: '15:00', end: '15:30', label: '오후 15:00~15:30', duration: 30 },
    { id: 'pm_4', start: '15:30', end: '16:00', label: '오후 15:30~16:00', duration: 30 },
    { id: 'pm_5', start: '16:00', end: '16:30', label: '오후 16:00~16:30', duration: 30 },
    { id: 'pm_6', start: '16:30', end: '17:00', label: '오후 16:30~17:00', duration: 30 },
    { id: 'pm_7', start: '17:00', end: '17:30', label: '오후 17:00~17:30', duration: 30 },
    { id: 'pm_8', start: '17:30', end: '18:00', label: '오후 17:30~18:00', duration: 30 },
    { id: 'evening_1', start: '18:00', end: '18:30', label: '저녁 18:00~18:30', duration: 30 },
    { id: 'evening_2', start: '18:30', end: '19:00', label: '저녁 18:30~19:00', duration: 30 },
    { id: 'evening_3', start: '19:00', end: '19:30', label: '저녁 19:00~19:30', duration: 30 },
    { id: 'evening_4', start: '19:30', end: '20:00', label: '저녁 19:30~20:00', duration: 30 },
    { id: 'evening_5', start: '20:00', end: '20:30', label: '저녁 20:00~20:30', duration: 30 },
    { id: 'evening_6', start: '20:30', end: '21:00', label: '저녁 20:30~21:00', duration: 30 },
    { id: 'night_1', start: '21:00', end: '21:30', label: '밤 21:00~21:30', duration: 30 },
    { id: 'night_2', start: '21:30', end: '22:00', label: '밤 21:30~22:00', duration: 30 },
    { id: 'night_3', start: '22:00', end: '22:30', label: '밤 22:00~22:30', duration: 30 },
    { id: 'night_4', start: '22:30', end: '23:00', label: '밤 22:30~23:00', duration: 30 },
    { id: 'night_5', start: '23:00', end: '23:30', label: '밤 23:00~23:30', duration: 30 },
    { id: 'night_6', start: '23:30', end: '24:00', label: '밤 23:30~24:00', duration: 30 }
  ]

  // 시간대를 그룹별로 정리
  const getTimeSlotGroups = () => {
    return [
      {
        label: '새벽 (1시간 단위)',
        icon: 'moon',
        color: 'slate',
        slots: timeSlotOptions.filter(s => s.id.startsWith('dawn'))
      },
      {
        label: '아침 (30분 단위)',
        icon: 'sunrise',
        color: 'amber',
        slots: timeSlotOptions.filter(s => s.id.startsWith('morning'))
      },
      {
        label: '오전 (30분 단위)',
        icon: 'sun',
        color: 'yellow',
        slots: timeSlotOptions.filter(s => s.id.startsWith('am'))
      },
      {
        label: '점심 (30분 단위)',
        icon: 'utensils',
        color: 'orange',
        slots: timeSlotOptions.filter(s => s.id.startsWith('lunch'))
      },
      {
        label: '오후 (30분 단위)',
        icon: 'cloud-sun',
        color: 'blue',
        slots: timeSlotOptions.filter(s => s.id.startsWith('pm'))
      },
      {
        label: '저녁 (30분 단위)',
        icon: 'sunset',
        color: 'purple',
        slots: timeSlotOptions.filter(s => s.id.startsWith('evening'))
      },
      {
        label: '밤 (30분 단위)',
        icon: 'moon-stars',
        color: 'indigo',
        slots: timeSlotOptions.filter(s => s.id.startsWith('night'))
      }
    ]
  }

  // 현재 활성 점프 확인 (내 점프)
  const checkActiveJump = async () => {
    if (!user.value) return null

    try {
      const { date: today } = getKoreanTime()

      const q = query(
        collection(db, 'jumpSlots'),
        where('partnerId', '==', user.value.uid),
        where('status', '==', 'active')
      )

      const snapshot = await getDocs(q)

      for (const doc of snapshot.docs) {
        const data = doc.data()
        // 아직 종료되지 않은 점프가 있는지 확인
        if (data.endDate >= today) {
          activeJump.value = { id: doc.id, ...data }
          return activeJump.value
        }
      }

      activeJump.value = null
      return null
    } catch (err) {
      console.error('활성 점프 확인 실패:', err)
      return null
    }
  }

  // 특정 현장의 특정 시간대 예약 여부 확인
  const isSlotBooked = async (propertyId, slotId, startDate, endDate) => {
    try {
      const q = query(
        collection(db, 'jumpSlots'),
        where('propertyId', '==', propertyId),
        where('status', '==', 'active'),
        where('slotId', '==', slotId)
      )

      const snapshot = await getDocs(q)

      for (const doc of snapshot.docs) {
        const data = doc.data()
        // 날짜 범위가 겹치는지 확인
        if (data.startDate <= endDate && data.endDate >= startDate) {
          return {
            booked: true,
            bookedBy: data.partnerDisplayName
          }
        }
      }

      return { booked: false }
    } catch (err) {
      console.error('슬롯 예약 확인 실패:', err)
      return { booked: false }
    }
  }

  // 특정 현장의 모든 예약된 슬롯 조회
  const getBookedSlots = async (propertyId, startDate, endDate) => {
    try {
      const q = query(
        collection(db, 'jumpSlots'),
        where('propertyId', '==', propertyId),
        where('status', '==', 'active')
      )

      const snapshot = await getDocs(q)
      const bookedSlotIds = []

      snapshot.docs.forEach(doc => {
        const data = doc.data()
        // 날짜 범위가 겹치는지 확인
        if (data.startDate <= endDate && data.endDate >= startDate) {
          bookedSlotIds.push({
            slotId: data.slotId,
            partnerId: data.partnerId,
            partnerDisplayName: data.partnerDisplayName
          })
        }
      })

      return bookedSlotIds
    } catch (err) {
      console.error('예약된 슬롯 조회 실패:', err)
      return []
    }
  }

  // 점프 신청 (무료, 1주일간 1개 슬롯)
  const applyJump = async (propertyId, propertyName, slotId, partnerDisplayName) => {
    if (!user.value) return { success: false, error: '로그인이 필요합니다.' }

    try {
      loading.value = true

      // 1. 현재 활성 점프가 있는지 확인
      const currentJump = await checkActiveJump()
      if (currentJump) {
        return {
          success: false,
          error: '이미 활성화된 점프가 있습니다. 기존 점프가 종료된 후 다시 신청해주세요.'
        }
      }

      // 2. 시작일과 종료일 계산 (오늘부터 1주일)
      const { date: today } = getKoreanTime()
      const startDate = today
      const endDate = getOneWeekLater(today)

      // 3. 해당 슬롯이 이미 예약되었는지 확인
      const bookingCheck = await isSlotBooked(propertyId, slotId, startDate, endDate)
      if (bookingCheck.booked) {
        return {
          success: false,
          error: `이미 ${bookingCheck.bookedBy}님이 해당 시간대를 예약했습니다.`
        }
      }

      // 4. 선택한 슬롯 정보 가져오기
      const selectedSlot = timeSlotOptions.find(s => s.id === slotId)
      if (!selectedSlot) {
        return { success: false, error: '유효하지 않은 시간대입니다.' }
      }

      // 5. 점프 슬롯 생성 (무료)
      const jumpDoc = await addDoc(collection(db, 'jumpSlots'), {
        propertyId,
        propertyName,
        partnerId: user.value.uid,
        partnerDisplayName: partnerDisplayName || '상담사',
        slotId: slotId,
        timeSlot: {
          start: selectedSlot.start,
          end: selectedSlot.end,
          label: selectedSlot.label,
          duration: selectedSlot.duration
        },
        startDate,
        endDate,
        status: 'active',
        createdAt: new Date().toISOString(),
        expiresAt: new Date(endDate + 'T23:59:59').toISOString()
      })

      // 6. 활성 점프 업데이트
      activeJump.value = {
        id: jumpDoc.id,
        propertyId,
        propertyName,
        slotId,
        timeSlot: selectedSlot,
        startDate,
        endDate,
        status: 'active'
      }

      return { success: true, jumpId: jumpDoc.id, startDate, endDate }
    } catch (err) {
      console.error('점프 신청 실패:', err)
      return { success: false, error: err.message }
    } finally {
      loading.value = false
    }
  }

  // 내 점프 목록 조회
  const loadMyJumps = async () => {
    if (!user.value) return

    try {
      loading.value = true
      const q = query(
        collection(db, 'jumpSlots'),
        where('partnerId', '==', user.value.uid),
        orderBy('createdAt', 'desc')
      )

      const snapshot = await getDocs(q)
      myJumps.value = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
    } catch (err) {
      console.error('점프 목록 로드 실패:', err)
      error.value = err.message
    } finally {
      loading.value = false
    }
  }

  // 점프 취소 (시작 전만 가능)
  const cancelJump = async (jumpId) => {
    if (!user.value) return { success: false, error: '로그인이 필요합니다.' }

    try {
      loading.value = true

      const jumpDoc = await getDoc(doc(db, 'jumpSlots', jumpId))
      if (!jumpDoc.exists()) {
        return { success: false, error: '점프 정보를 찾을 수 없습니다.' }
      }

      const jumpData = jumpDoc.data()

      // 시작일 확인 (한국 시간 기준)
      const { date: today } = getKoreanTime()
      if (jumpData.startDate <= today) {
        return { success: false, error: '이미 시작된 점프는 취소할 수 없습니다.' }
      }

      // 상태 변경
      await updateDoc(doc(db, 'jumpSlots', jumpId), {
        status: 'cancelled',
        cancelledAt: new Date().toISOString()
      })

      // 활성 점프 초기화
      if (activeJump.value?.id === jumpId) {
        activeJump.value = null
      }

      return { success: true }
    } catch (err) {
      console.error('점프 취소 실패:', err)
      return { success: false, error: err.message }
    } finally {
      loading.value = false
    }
  }

  // 점프 삭제 (모든 상태에서 가능 - 기록 삭제)
  const deleteJump = async (jumpId) => {
    if (!user.value) return { success: false, error: '로그인이 필요합니다.' }

    try {
      loading.value = true

      const jumpDoc = await getDoc(doc(db, 'jumpSlots', jumpId))
      if (!jumpDoc.exists()) {
        return { success: false, error: '점프 정보를 찾을 수 없습니다.' }
      }

      // 문서 삭제
      await deleteDoc(doc(db, 'jumpSlots', jumpId))

      // 활성 점프 초기화
      if (activeJump.value?.id === jumpId) {
        activeJump.value = null
      }

      return { success: true }
    } catch (err) {
      console.error('점프 삭제 실패:', err)
      return { success: false, error: err.message }
    } finally {
      loading.value = false
    }
  }

  // 현재 활성 점프 조회 (특정 현장) - 한국 시간 기준
  const getActiveJumpsForProperty = async (propertyId) => {
    try {
      const { date: today, time: currentTime } = getKoreanTime()

      const q = query(
        collection(db, 'jumpSlots'),
        where('propertyId', '==', propertyId),
        where('status', '==', 'active')
      )

      const snapshot = await getDocs(q)
      const activeJumps = []

      snapshot.docs.forEach(doc => {
        const data = doc.data()
        // 현재 날짜가 점프 기간 내인지 확인
        if (data.startDate <= today && data.endDate >= today) {
          // 현재 시간이 점프 시간대 내인지 확인
          const slot = data.timeSlot
          if (currentTime >= slot.start && currentTime < slot.end) {
            activeJumps.push({
              id: doc.id,
              ...data
            })
          }
        }
      })

      return activeJumps
    } catch (err) {
      console.error('활성 점프 조회 실패:', err)
      return []
    }
  }

  // 점프 상태 확인 (active, expired, cancelled)
  const getJumpStatus = (jump) => {
    if (jump.status === 'cancelled') return 'cancelled'

    const { date: today } = getKoreanTime()
    if (jump.endDate < today) return 'expired'

    return 'active'
  }

  return {
    myJumps,
    activeJump,
    loading,
    error,
    timeSlotOptions,
    getTimeSlotGroups,
    checkActiveJump,
    isSlotBooked,
    getBookedSlots,
    applyJump,
    loadMyJumps,
    cancelJump,
    deleteJump,
    getActiveJumpsForProperty,
    getJumpStatus,
    getKoreanTime,
    getOneWeekLater
  }
}
